import React from 'react'
import { Columns, Element, Icon } from 'react-bulma-components'
import { User, UserAssociation } from '../../api/users'
import { useModal } from '../modal'
import ResourceActions from '../resources/resource-actions'
import useStore from '../../store'
import {
  getWorkerAndOrganizationAssociation,
  getActiveContractualSchedule,
} from '../../utils/users'
import { CheckIcon } from '../icons'
import { BanIcon } from '../icons'
import ShowQrCodeButton from '../users/buttons/show-qr-code'
import useQrCode from '../../hooks/use-qr-code'
import RoleStatus from '../users/role-status'
import DiscontinueAssociationButton from '../users/buttons/discontinue-association'
import { Organization } from '../../api/organizations'
import RoleStatusDescription from '../users/role-status-description'
import EditUserButton from '../users/buttons/edit-user'
import ActiveEmployerCollaborationButton from '../users/buttons/active-employer-collaboration'
import Protected from '../protected/protected'
import { useWorkerCountersQuery } from '../../queries/worker-counters'
import { formatDuration } from '../../utils/date'
import WorkerCounterModal from '../worker-counters/worker-counter-modal'
import { WorkerCounter } from '../../api/worker-counters'

interface WorkerActionsProps {
  worker: User
  isEmployerCollaborationInactive?: boolean
  isExternalWorker?: boolean
  association?: UserAssociation
}

/**
 * Component to display actions available for a worker
 */
const WorkerActions: React.FC<WorkerActionsProps> = ({
  worker,
  isEmployerCollaborationInactive,
  isExternalWorker,
  association,
}) => {
  const actions = useActions(worker, isEmployerCollaborationInactive, isExternalWorker, association)

  return <ResourceActions actions={actions} />
}

export default WorkerActions

interface Action {
  title: React.ReactNode
  description?: string
  content: React.ReactNode
}

const useActions = (
  worker: User,
  isEmployerCollaborationInactive?: boolean,
  isExternalWorker?: boolean,
  association?: UserAssociation,
): Action[] => {
  const modal = useModal()
  const currentOrganization = useStore(state => state.session.currentOrganization)
  const workerCounters = useWorkerCountersQuery(
    { worker: worker._id },
    { enabled: Boolean(worker._id) },
  )
  const qrCode = useQrCode(worker._id, isExternalWorker)
  const workerAssociation =
    currentOrganization && getWorkerAndOrganizationAssociation(worker, currentOrganization)
  const isActive = workerAssociation?.status === 'accepted'

  // State pour gérer la modale des compteurs
  const [selectedWorkerCounter, setSelectedWorkerCounter] = React.useState<WorkerCounter | null>(
    null,
  )

  // Réinitialiser le worker counter sélectionné quand la modale se ferme
  React.useEffect(() => {
    if (!modal.isDisplayed) {
      setSelectedWorkerCounter(null)
    }
  }, [modal.isDisplayed])

  return React.useMemo(
    () =>
      [
        isExternalWorker && {
          title: (
            <Icon.Text>
              <Icon.Text>Collaboration avec l'intérimaire</Icon.Text>
              <Icon color={isEmployerCollaborationInactive ? 'danger' : 'success'}>
                {isEmployerCollaborationInactive ? <BanIcon /> : <CheckIcon />}
              </Icon>
            </Icon.Text>
          ),
          content: (
            <>
              {isEmployerCollaborationInactive ? (
                <>
                  <Element renderAs="p" mb={3}>
                    Vous avez signalé aux agences que vous ne souhaitez plus travailler avec cet
                    intérimaire.
                  </Element>
                  <ActiveEmployerCollaborationButton user={worker} isInactive={true} />
                </>
              ) : (
                <>
                  <Element renderAs="p" mb={3}>
                    Si vous ne souhaitez plus travailler avec cet intérimaire, vous pouvez le
                    signaler aux agences ici.
                    <br /> Vous pourrez le réintégrer à tout moment.
                  </Element>
                  <ActiveEmployerCollaborationButton user={worker} isInactive={false} />
                </>
              )}
            </>
          ),
        },
        !isExternalWorker &&
          association && {
            title: 'Informations',
            content: (
              <>
                <Element renderAs="p" mb={3}>
                  <Columns mt={3}>
                    <Protected roles={['employerMember']}>
                      {(() => {
                        const activeSchedule = getActiveContractualSchedule(association)
                        return (
                          <>
                            <Columns.Column size={3} py={1}>
                              <Element renderAs="strong">Heures mensuelles: </Element>
                              {activeSchedule?.monthlyHoursWorked ?? 'N/A'} h/mois
                            </Columns.Column>
                            <Columns.Column size={3} py={1}>
                              <Element renderAs="strong">Heures hebdomadaires: </Element>
                              {activeSchedule?.weeklyHoursWorked ?? 'N/A'} h/sem
                            </Columns.Column>
                          </>
                        )
                      })()}
                    </Protected>
                    <Columns.Column size={12} py={1}>
                      <Element renderAs="strong">Informations supplémentaires: </Element>
                      {association.internalComment || 'N/A'}
                    </Columns.Column>
                    <Columns.Column size={12} py={1}>
                      <Element renderAs="strong">Informations interne: </Element>
                      {association.internalInformation || 'N/A'}
                    </Columns.Column>
                  </Columns>
                  <EditUserButton
                    user={worker}
                    userRoleKind="worker"
                    label="Modifier"
                    color={'primary'}
                  />
                </Element>
              </>
            ),
          },
        workerCounters.data?.[0] &&
          !isExternalWorker && {
            title: 'Compteurs',
            content: (
              <>
                <Element renderAs="p" mb={3}>
                  {workerCounters.data?.map(counter => (
                    <Element key={counter._id} mb={2}>
                      <Element renderAs="strong">{counter.template.name}: </Element>
                      <Element
                        renderAs="span"
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => {
                          setSelectedWorkerCounter(counter)
                          modal.setIsDisplayed(true)
                        }}
                      >
                        {formatDuration(counter.currentValue)}
                      </Element>
                    </Element>
                  ))}
                </Element>

                {/* Modal pour les détails du worker counter */}
                <WorkerCounterModal
                  workerCounter={selectedWorkerCounter || undefined}
                  isDisplayed={modal.isDisplayed}
                  setIsDisplayed={modal.setIsDisplayed}
                />
              </>
            ),
          },
        {
          title: 'Impression QR code',
          content: (
            <>
              <Element renderAs="p" mb={3}>
                {isExternalWorker && !qrCode ? (
                  <>
                    Pour les intérimaires, le QR code n'est disponible que pendant une mission
                    active ou dans les 3 jours précédant son début.
                  </>
                ) : (
                  <>
                    Vous pouvez imprimer le QR code du travailleur afin qu'il puisse pointer sans
                    son smartphone. <br />
                    Cependant, il est préférable pour des raisons de sécurité et de suivi que les
                    travailleurs utilisent l'application mobile.
                  </>
                )}
              </Element>
              {qrCode && (
                <ShowQrCodeButton
                  user={worker}
                  color="primary"
                  label="Afficher le QR code"
                  canRegenerate={false}
                  isExternalWorker={isExternalWorker}
                />
              )}
            </>
          ),
        },
        !isExternalWorker &&
          association && {
            title: (
              <Icon.Text>
                Collaboration
                <RoleStatus role={association} />
              </Icon.Text>
            ),
            content: (
              <>
                <Element renderAs="p" mb={3}>
                  <RoleStatusDescription role={association} />
                </Element>
                {(association.status === 'accepted' || association.status === 'pending') && (
                  <DiscontinueAssociationButton
                    user={worker}
                    color="danger"
                    roleKind="worker"
                    organization={currentOrganization as Organization}
                  />
                )}
              </>
            ),
          },
      ].filter(v => v) as Action[],
    [
      worker,
      modal,
      isActive,
      isExternalWorker,
      isEmployerCollaborationInactive,
      qrCode,
      association,
    ],
  )
}
